import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "../contexts/LanguageContext";
import { Reveal, LineReveal } from "./motion/Reveal";

const CHAPTERS_EN = [
  {
    n: "01",
    kicker: "Position",
    title: "Every SME deserves a CFO-grade lens.",
    body:
      "You built the business. We stress-test the finance function that keeps it standing — cash flow, controls, compliance, capital.",
  },
  {
    n: "02",
    kicker: "Method",
    title: "Diagnostic first. Opinions later.",
    body:
      "Our 12-point health check maps five financial pillars against KSA benchmarks in 4 minutes — no fluff, no surveys, no sales pressure.",
  },
  {
    n: "03",
    kicker: "Craft",
    title: "Built inside Saudi Arabia, for Saudi Arabia.",
    body:
      "ZATCA e-invoicing, Zakat, Vision 2030 and localisation — we read the ground rules first, then translate the P&L for the CEO.",
  },
  {
    n: "04",
    kicker: "Outcome",
    title: "Numbers that survive a board meeting.",
    body:
      "You walk away with a report your bank respects, your investor trusts, and your finance team can execute against on Monday morning.",
  },
];

const CHAPTERS_AR = [
  {
    n: "٠١",
    kicker: "الموقع",
    title: "كل شركة صغيرة ومتوسطة تستحق عدسة مالية بمستوى المدير المالي.",
    body:
      "أنت بنيت العمل، ونحن نختبر قوة الوظيفة المالية التي تحافظ على استقراره: التدفق النقدي، الضوابط، الامتثال، ورأس المال.",
  },
  {
    n: "٠٢",
    kicker: "المنهجية",
    title: "التشخيص أولًا، الآراء لاحقًا.",
    body:
      "اختبار الصحة المكوّن من 12 سؤالًا يقيس خمس ركائز مالية مقابل معايير المملكة في 4 دقائق — بلا حشو أو ضغط بيعي.",
  },
  {
    n: "٠٣",
    kicker: "الحرفة",
    title: "صُنع داخل المملكة، للمملكة.",
    body:
      "فوترة زاتكا الإلكترونية، الزكاة، رؤية 2030، والتوطين — نقرأ قواعد اللعبة أولًا، ثم نترجم قائمة الدخل لرئيس التنفيذي.",
  },
  {
    n: "٠٤",
    kicker: "النتيجة",
    title: "أرقام تصمد أمام مجلس الإدارة.",
    body:
      "ستحصل على تقرير يحترمه بنكك، ويثق به مستثمرك، ويستطيع فريقك المالي تنفيذه صباح الاثنين.",
  },
];

export default function Manifesto() {
  const { lang } = useLang();
  const chapters = lang === "ar" ? CHAPTERS_AR : CHAPTERS_EN;
  const rootRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: rootRef, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section ref={rootRef} id="manifesto" className="relative bg-cb-cream py-24 md:py-40 overflow-hidden" data-testid="manifesto">
      {/* subtle parallax script mark */}
      <motion.div
        aria-hidden="true"
        style={{ y: bgY, fontFamily: "'Playfair Display', serif", color: "rgba(10, 17, 40, 0.04)" }}
        className="pointer-events-none absolute -end-32 md:-end-56 top-1/2 -translate-y-1/2 text-[30vw] leading-none italic font-light select-none"
      >
        Bay
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
        <Reveal className="max-w-3xl">
          <div className="text-[11px] tracking-[0.35em] uppercase text-cb-gold mb-6">Manifesto</div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-cb-primary" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            <LineReveal
              text={
                lang === "ar"
                  ? "أرقام واضحة. قرارات جريئة."
                  : "Clear numbers. Bold decisions."
              }
              stagger={0.08}
            />
          </h2>
        </Reveal>

        <div className="mt-16 md:mt-24 grid gap-0 divide-y divide-cb-primary/10">
          {chapters.map((c, i) => (
            <Reveal
              key={c.n}
              delay={0.05 * i}
              className="grid md:grid-cols-12 gap-6 md:gap-12 py-10 md:py-14 items-start"
              data-testid={`chapter-${c.n}`}
            >
              <div className="md:col-span-2 flex items-baseline gap-3">
                <span
                  className="text-3xl md:text-4xl text-cb-gold"
                  style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 300 }}
                >
                  {c.n}
                </span>
                <span className="text-[11px] tracking-[0.3em] uppercase text-cb-secondary">
                  {c.kicker}
                </span>
              </div>
              <div className="md:col-span-6">
                <h3
                  className="text-2xl md:text-3xl text-cb-primary leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                >
                  {c.title}
                </h3>
              </div>
              <div className="md:col-span-4">
                <p className="text-cb-secondary leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
